import React, { useState, useEffect, useRef, useCallback } from 'react';
import jwtDecode from 'jwt-decode';
import './ApplicationView.scss';
import { getCurrentUserTokens } from '../../../utils/aws';
import config from '../../../config/config';
import MerchantDetailsCard from '../MerchantDetailsCard';
import { STATUS_MAP } from '../MerchantDetailsHeader/MerchantDetailsHeader';
import _ from 'lodash';

/**
 * Decodes the JWT token to extract user information
 * @param {string} idToken - The JWT id token to decode
 * @returns {Object} Object containing userId, username, and expiration info
 */
const decodeUserInfo = (idToken) => {
    let userId = '';
    let username = '';
    let awsExp = '';
    let awsExpRefresh = '';

    if (!idToken) {
        return { userId, username, awsExp, awsExpRefresh };
    }

    try {
        const decodedToken = jwtDecode(idToken);
        userId = decodedToken.sub || '';
        username = decodedToken['cognito:username'] || decodedToken.email || '';
        awsExp = decodedToken.exp ? decodedToken.exp.toString() : '';
    } catch (decodeError) {
        // eslint-disable-next-line no-console
        console.error('Failed to decode token:', decodeError);
    }

    return { userId, username, awsExp, awsExpRefresh };
};

/**
 * ApplicationView component that displays an iframe for the online application
 * Handles postMessage communication with the iframe to send authentication tokens
 * @param {Object} prevet - Prevet data object (currently unused but kept for future use)
 */
export const ApplicationView = ({ merchantDetails }) => {
    const [status, setStatus] = useState(merchantDetails.detail?.status || '');
    const [iframeHeight, setIframeHeight] = useState(1000); // Initial minimum height

    useEffect(() => {
        setStatus(merchantDetails.detail?.status);
    }, [merchantDetails.detail?.status]);

    const iframeRef = useRef(null);
    const intervalRef = useRef(null);
    const resetInterval = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };

    /**
     * Sends authentication tokens to the iframe
     */
    const sendTokensToIframe = useCallback(async () => {
        if (!iframeRef.current || !iframeRef.current.contentWindow) {
            return;
        }

        try {
            const tokens = await getCurrentUserTokens();
            const idToken = tokens.idToken || '';
            const accessToken = tokens.accessToken || '';
            const refreshToken = tokens.refreshToken || '';
            const { awsExp, awsExpRefresh } = decodeUserInfo(idToken);

            iframeRef.current.contentWindow.postMessage(
                {
                    token: idToken,
                    access_token: accessToken,
                    refresh_token: refreshToken,
                    aws_exp: awsExp,
                    aws_expRefresh: awsExpRefresh
                },
                '*'
            );
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to get user tokens:', error);
        }
    }, []);

    /**
     * Handles refresh messages from the iframe
     */
    const handleRefreshMessage = useCallback(async () => {
        if (!iframeRef.current || !iframeRef.current.contentWindow) {
            return;
        }

        resetInterval();

        await sendTokensToIframe();

        intervalRef.current = setInterval(async () => {
            await sendTokensToIframe();
        }, 5 * 60 * 1000); // 5 minutes in milliseconds
    }, [sendTokensToIframe]);

    /**
     * Handles messages received from the iframe
     * Sends authentication tokens or handles refresh requests
     * Also handles height resize messages from the iframe
     * @param {MessageEvent} event - The message event from the iframe
     */
    const handleMessage = useCallback(async (event) => {
        if (!iframeRef.current || !iframeRef.current.contentWindow) {
            return;
        }

        // Check if message contains height for dynamic iframe resizing
        if (event.data && typeof event.data.height === 'number') {
            setIframeHeight(event.data.height);

            return;
        }

        // Check if message has refresh property
        if (event.data && event.data.refresh) {
            handleRefreshMessage();
        } else {
            await sendTokensToIframe();
        }
    }, [handleRefreshMessage, sendTokensToIframe]);

    /**
     * Set up message listener when component mounts
     * Clean up listener when component unmounts
     */
    useEffect(() => {
        window.addEventListener('message', handleMessage);

        // Send tokens when iframe loads and set up periodic refresh
        const initializeTokens = async () => {
            await new Promise((resolve) => setTimeout(resolve, 500));

            await sendTokensToIframe();

            intervalRef.current = setInterval(async () => {
                await sendTokensToIframe();
            }, 5 * 60 * 1000);
        };

        initializeTokens();

        return () => {
            window.removeEventListener('message', handleMessage);
            resetInterval();
        };
    }, [handleMessage, sendTokensToIframe]);

    // Get the online app URL from environment variable
    const onlineAppUrl = `${config.onlineAppUrl}/${_.get(merchantDetails, 'detail.onlineApplicationId', '')}?external`;
    const startOpen = STATUS_MAP['showApp'].includes(status) ? true : false;
    const showAppDownload = STATUS_MAP['showMid'].includes(status) ? true : false;

    const renderAppDocuments = (docs) => {
        if (!docs || !Array.isArray(docs) || docs.length === 0) {
            return null;
        }

        return (
            <div className="application-view-documents-container">
                {docs.map((doc) => (
                    <div key={doc.datetimestamp}>
                        <a className="application-view-document-link" href={doc.img_url} download>{doc.filename}</a>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <>
            {showAppDownload ? (
                <MerchantDetailsCard title="Application" startOpen={false}>
                    {renderAppDocuments(_.get(merchantDetails, 'merchantData.attachments.applicationAttachments', []))}
                </MerchantDetailsCard>
            ) : (
                <>
                    {_.get(merchantDetails, 'detail.onlineApplicationId', '') && (
                        <MerchantDetailsCard title="Application" startOpen={startOpen}>
                            <div className="application-view-content">
                                <iframe
                                    ref={iframeRef}
                                    id="online-app"
                                    name="Online-App"
                                    src={onlineAppUrl}
                                    width="100%"
                                    height={iframeHeight}
                                    style={{ minHeight: '1000px' }}
                                    frameBorder="0"
                                    title="Online Application"
                                    loading="lazy"
                                />
                            </div>
                        </MerchantDetailsCard>
                    )}
                </>
            )}
        </>
    );
};

export default ApplicationView;
